import { ArrowRight, Check, ChevronDown, ChevronRight, CircleAlert, Info } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, Ref, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { bidiIsolate } from '@shared/ai-data-policy/names';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@shared/components/ui';
import { cn } from '@shared/components/ui/cn';

import { levelName } from '../data/level-text';
import { IN_HOUSE_GROUP, type ModelOption, type ModelOptionGroup } from '../data/models';
import { barsOf, capOf, levelAfterPick, levelOffer, type Level } from '../data/thinking-levels';
import type { PickerPolicy } from '../data-policy/picker-policy';
import type { ModelControls } from '../hooks/use-model-controls';

import { LevelGlyph } from './LevelGlyph';
import { ProviderLogo } from './ModelMark';
import {
  boxAnchor,
  GAP,
  GUTTER,
  panelSide,
  viewportHeight,
  type PanelSide,
} from './popover-placement';

/** Below this composer width (28rem, the drawn composer's) the Model chip shows its logo only. */
export const CHIP_LABEL_MIN_WIDTH = 448;

/** The picker's fixed height: four two-line rows under the pane's heading. */
const PICKER_HEIGHT = 232;

interface ModelPickerProps {
  controls: ModelControls;
  /** The composer's lock. */
  disabled?: boolean;
  /** The composer is narrower than CHIP_LABEL_MIN_WIDTH: the chip shows its logo only. */
  compact?: boolean;
  /** The composer box: the popover's anchor. */
  boxRef: RefObject<HTMLElement | null>;
  /** Controlled by Composer, so the Unavailable note's "Choose model" can open it. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A model other than the shown one was picked (click or Enter). Never called for the current one. */
  onPick: (option: ModelOption) => void;
  policy?: PickerPolicy;
}

function setRef<T extends HTMLElement>(map: Map<string, T>, key: string, el: T | null) {
  if (el) map.set(key, el);
  else map.delete(key);
}

/**
 * The composer's Model chip ([provider logo][model] ▾) and the picker it opens (DEV-4575
 * Option C): the providers on the left, a vertical tab list, in-house first; the selected
 * provider's models on the right, a list box whose rows each say how far the model thinks
 * ("Up to {cap}") or that picking it lowers the level shown now ("Lowers thinking to {level}").
 * Browsing the providers applies nothing; a pick of another model calls `onPick` and closes.
 *
 * The popover is a fixed 448 × 232, anchored to the composer box's inline end, below it when it
 * fits there, else above it; in the side panel at its default width it overhangs the panel on
 * purpose, so a row's second line never truncates. Under a partner override, and in a connected
 * session, the chip is a static label: there is nothing to pick. While the chat's model is
 * unavailable (no longer listed), the chip shows an alert in its logo's place, and the picker
 * opens on that model's provider with focus on its first model.
 */
export function ModelPicker({
  controls,
  disabled = false,
  compact = false,
  boxRef,
  open,
  onOpenChange,
  onPick,
  policy,
}: ModelPickerProps) {
  const { t, i18n } = useTranslation('ai-cio');
  const rtl = i18n.dir() === 'rtl';
  const { models, picker, unavailable, shown, level, choice, refresh } = controls;
  const scale = models?.thinking_scale;
  /** The provider the pane shows once the user moves off the one it opened on. */
  const [chosenGroup, setChosenGroup] = useState<string | null>(null);
  /** The row the list's roving focus is on. */
  const [focusedOption, setFocusedOption] = useState<string | null>(null);
  const [side, setSide] = useState<PanelSide>('bottom');
  const chipRef = useRef<HTMLButtonElement>(null);
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());
  const optionRefs = useRef(new Map<string, HTMLDivElement>());
  const footerRef = useRef<HTMLDivElement>(null);
  const anchor = useMemo(() => ({ current: boxAnchor(boxRef, chipRef) }), [boxRef]);

  const uid = useId();
  const tabpanelId = `${uid}-tabpanel`;
  const headingId = `${uid}-heading`;
  const ruleId = `${uid}-rule`;
  const tabId = (group: string) => `${uid}-tab-${group}`;
  const labelId = (index: number) => `${uid}-label-${String(index)}`;
  const lineId = (index: number) => `${uid}-line-${String(index)}`;

  const groups: ModelOptionGroup[] = picker ? [...picker.inHouse, ...picker.thirdParty] : [];
  /** It opens on the shown model's provider; while the chat's model is unavailable, on its own. */
  const ownGroup = unavailable ? unavailable.groupId : shown?.group.id;
  const startGroup = groups.some((g) => g.id === ownGroup) ? ownGroup : groups[0]?.id;
  const activeGroup = groups.find((g) => g.id === (chosenGroup ?? startGroup)) ?? groups[0];
  const options = activeGroup?.options ?? [];
  const currentId = shown && options.some((o) => o.id === shown.id) ? shown.id : null;
  const tabbableId =
    focusedOption && options.some((o) => o.id === focusedOption)
      ? focusedOption
      : (currentId ?? options[0]?.id ?? null);
  const nameOf = (l: Level) => levelName(t, l.id, l.label);
  const label = unavailable?.label ?? shown?.label ?? t('picker.model', 'Model');

  function setOpen(next: boolean) {
    // Each opening starts on the shown model's provider.
    if (!next) {
      setChosenGroup(null);
      setFocusedOption(null);
    }
    onOpenChange(next);
  }

  function selectGroup(id: string) {
    setChosenGroup(id);
    setFocusedOption(null);
  }

  function focusOption(id: string) {
    setFocusedOption(id);
    optionRefs.current.get(id)?.focus();
  }

  /**
   * As it opens: a fresh read of /models, its side, and focus on the shown model; while the
   * chat's model is unavailable, on its provider's first model; else on the provider's tab.
   */
  function handleOpened() {
    refresh();
    policy?.refresh();
    const r = anchor.current.getBoundingClientRect();
    const height = PICKER_HEIGHT + (footerRef.current?.offsetHeight ?? 0);
    setSide(panelSide(height, r.top - GAP - GUTTER, viewportHeight() - r.bottom - GAP - GUTTER));
    const first = options[0];
    if (currentId) focusOption(currentId);
    else if (unavailable && first) focusOption(first.id);
    else if (activeGroup) tabRefs.current.get(activeGroup.id)?.focus();
  }

  function pick(option: ModelOption) {
    if (option.id !== shown?.id) onPick(option);
    setOpen(false);
  }

  function handleTabKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (!activeGroup) return;
    if (e.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) {
      e.preventDefault();
      if (tabbableId) focusOption(tabbableId);
      return;
    }
    const ids = groups.map((g) => g.id);
    const at = ids.indexOf(activeGroup.id);
    let next: number;
    if (e.key === 'ArrowDown') next = (at + 1) % ids.length;
    else if (e.key === 'ArrowUp') next = (at - 1 + ids.length) % ids.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = ids.length - 1;
    else return;
    const id = ids[next];
    if (id === undefined) return;
    e.preventDefault();
    selectGroup(id);
    tabRefs.current.get(id)?.focus();
  }

  function handleOptionKeyDown(e: KeyboardEvent<HTMLDivElement>, index: number) {
    const option = options[index];
    if (!option) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(option);
      return;
    }
    if (e.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) {
      e.preventDefault();
      if (activeGroup) tabRefs.current.get(activeGroup.id)?.focus();
      return;
    }
    let next: number;
    if (e.key === 'ArrowDown') next = Math.min(index + 1, options.length - 1);
    else if (e.key === 'ArrowUp') next = Math.max(index - 1, 0);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = options.length - 1;
    else return;
    e.preventDefault();
    const target = options[next];
    if (target) focusOption(target.id);
  }

  /** A row's second line: what picking it does to the level shown now, else the model's cap. */
  function lineTwo(option: ModelOption): { text: string; cap: Level } | null {
    if (option.thinking === null) return null;
    const cap = capOf(levelOffer(scale, option.thinking));
    if (!cap) return null;
    const landing = levelAfterPick(scale, option.thinking, level, choice);
    const text =
      level && landing && landing.rank < level.rank
        ? t('picker.lowers', 'Lowers thinking to {{level}}', { level: nameOf(landing) })
        : t('picker.upTo', 'Up to {{level}}', { level: nameOf(cap) });
    return { text, cap };
  }

  if (picker && (picker.override || picker.connected)) {
    // Nothing to pick: the partner's model, or the deployment's in a connected session.
    return (
      <span
        title={picker.override ? t('picker.orgSet', 'Model set by your organization') : label}
        className="flex h-8 min-w-0 items-center gap-1.5 px-2 text-sm text-foreground"
      >
        <ProviderLogo group={shown?.group ?? null} />
        <span className="min-w-0 truncate">{label}</span>
      </span>
    );
  }

  const chipName = unavailable
    ? t('picker.modelChipUnavailable', 'Model: {{model}}, unavailable right now', {
        model: unavailable.label,
      })
    : shown
      ? t('picker.modelChip', 'Model: {{model}}', { model: shown.label })
      : t('picker.model', 'Model');

  function tab(group: ModelOptionGroup) {
    const selected = group.id === activeGroup?.id;
    return (
      <button
        key={group.id}
        ref={(el) => {
          setRef(tabRefs.current, group.id, el);
        }}
        id={tabId(group.id)}
        type="button"
        role="tab"
        aria-selected={selected}
        aria-controls={tabpanelId}
        tabIndex={selected ? 0 : -1}
        onClick={() => {
          selectGroup(group.id);
        }}
        onKeyDown={handleTabKeyDown}
        className={cn(
          'flex h-8 w-full shrink-0 items-center gap-2.5 rounded-md px-2 text-sm text-foreground outline-none hover:bg-accent focus-visible:ring-1 focus-visible:ring-ring',
          selected && 'bg-accent font-medium',
        )}
      >
        <ProviderLogo group={group} />
        <span className="min-w-0 flex-1 truncate text-start">{group.label}</span>
        {selected && <ChevronRight aria-hidden className="size-3.5 shrink-0 rtl:rotate-180" />}
      </button>
    );
  }

  const count = options.length;

  const panes = (
    <>
      <div className="flex w-43 shrink-0 flex-col py-1">
        <p
          aria-hidden="true"
          className="flex h-8 shrink-0 items-center px-3 text-xs text-muted-foreground"
        >
          {t('picker.provider', 'Provider')}
        </p>
        <div
          role="tablist"
          aria-orientation="vertical"
          aria-label={t('picker.provider', 'Provider')}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-1"
        >
          {picker?.inHouse.map(tab)}
          {picker && picker.inHouse.length > 0 && picker.thirdParty.length > 0 && (
            <Separator className="-mx-1 my-1 data-[orientation=horizontal]:w-auto" />
          )}
          {picker?.thirdParty.map(tab)}
        </div>
      </div>
      <div aria-hidden="true" className="w-px shrink-0 bg-border" />
      <div
        id={tabpanelId}
        role="tabpanel"
        aria-labelledby={activeGroup ? tabId(activeGroup.id) : undefined}
        className="flex min-w-0 flex-1 flex-col p-1"
      >
        <p
          id={headingId}
          className="flex h-8 shrink-0 items-center pe-2 ps-8 text-xs text-muted-foreground"
        >
          {activeGroup?.id === IN_HOUSE_GROUP
            ? t('picker.inHouseCount', 'Runs inside Investbanq · {{count}} models', { count })
            : t('picker.commercialCount', 'Commercial · {{count}} models', { count })}
        </p>
        <div
          role="listbox"
          aria-labelledby={headingId}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        >
          {options.map((option, index) => {
            const current = option.id === currentId;
            const line = lineTwo(option);
            return (
              <div
                key={option.id}
                ref={(el) => {
                  setRef(optionRefs.current, option.id, el);
                }}
                role="option"
                aria-selected={current}
                aria-labelledby={labelId(index)}
                aria-describedby={line ? lineId(index) : undefined}
                tabIndex={option.id === tabbableId ? 0 : -1}
                onClick={() => {
                  pick(option);
                }}
                onKeyDown={(e) => {
                  handleOptionKeyDown(e, index);
                }}
                onFocus={() => {
                  setFocusedOption(option.id);
                }}
                className={cn(
                  'flex h-12 w-full cursor-pointer items-center gap-2 rounded-md px-2 text-start outline-none hover:bg-accent',
                  // F8: after Choose model the row focus lands on is filled, to show the model
                  // it offers, however the picker opened; S1: an opening on the current model
                  // shows its check alone, and a keyboard focus the fill.
                  unavailable ? 'focus:bg-accent' : 'focus-visible:bg-accent',
                )}
              >
                {current ? (
                  <Check aria-hidden className="size-4 shrink-0" />
                ) : (
                  <span aria-hidden="true" className="size-4 shrink-0" />
                )}
                <span className="min-w-0 flex-1">
                  <span id={labelId(index)} className="block truncate text-sm text-foreground">
                    {option.label}
                  </span>
                  {line && (
                    <span
                      id={lineId(index)}
                      className="block truncate text-xs text-muted-foreground"
                    >
                      {line.text}
                    </span>
                  )}
                </span>
                {line && <LevelGlyph filled={barsOf(line.cap.rank)} tone="cap" />}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={chipRef}
          type="button"
          disabled={disabled || models === null || groups.length === 0}
          aria-label={chipName}
          title={chipName}
          className={cn(
            'flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md text-sm text-foreground outline-none transition-colors hover:bg-accent focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:bg-accent',
            // The drawn 10 px padding; 6 px in a narrow composer, where the footer is short.
            compact ? 'shrink-0 px-1.5' : 'min-w-0 px-2.5',
          )}
        >
          {unavailable ? (
            <CircleAlert aria-hidden className="size-4 shrink-0" />
          ) : (
            models !== null && <ProviderLogo group={shown?.group ?? null} />
          )}
          {!compact && <span className="min-w-0 truncate">{label}</span>}
          <ChevronDown aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverAnchor virtualRef={anchor} />
      <PopoverContent
        side={side}
        align="end"
        sideOffset={GAP}
        collisionPadding={GUTTER}
        updatePositionStrategy="always"
        aria-label={t('picker.dialog', 'Choose a model')}
        // Portalled outside the AI CIO panel, it is still its surface: a transactions side panel
        // under it (SidePanel's onInteractOutside) stays open while the picker is used.
        data-ai-cio-surface
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          handleOpened();
        }}
        onEscapeKeyDown={(e) => {
          // Escape closes the picker only: it never reaches the app's Escape that closes the
          // AI CIO panel.
          e.stopPropagation();
        }}
        // Unpadded, so the rail's separator and the divider run to the popover's edges (F2); the
        // rail and the pane keep their rows 4 px in.
        className={cn(
          'max-h-[var(--radix-popover-content-available-height)] w-[448px] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden rounded-lg p-0 text-start',
          policy ? 'flex flex-col' : 'flex h-58',
        )}
        aria-describedby={policy ? ruleId : undefined}
      >
        {policy ? (
          <>
            <div className="flex h-58 min-h-0 shrink">{panes}</div>
            <PolicyFooter
              ref={footerRef}
              policy={policy}
              shown={shown}
              ruleId={ruleId}
              onAction={() => {
                setOpen(false);
              }}
            />
          </>
        ) : (
          panes
        )}
      </PopoverContent>
    </Popover>
  );
}

interface PolicyFooterProps {
  policy: PickerPolicy;
  shown: ModelOption | null;
  ruleId: string;
  onAction: () => void;
  ref?: Ref<HTMLDivElement>;
}

function PolicyFooter({ policy, shown, ruleId, onAction, ref }: PolicyFooterProps) {
  const { t, i18n } = useTranslation('ai-cio');
  const internal = policy.rule === 'internal';
  const source = !policy.decided
    ? t('picker.policy.sourceDefault', 'Default until an admin chooses a data policy.')
    : policy.setByName
      ? t('picker.policy.sourceWorkspace', 'Workspace policy · set by {{name}}', {
          name: bidiIsolate(policy.setByName, i18n.dir() === 'rtl'),
        })
      : t('picker.policy.sourceWorkspacePlain', 'Workspace policy');
  const offerNewChat =
    internal &&
    policy.chatHasInvestorData &&
    policy.onStartNewChat !== undefined &&
    shown !== null &&
    shown.group.id !== IN_HOUSE_GROUP;
  const choose = policy.canChange && !policy.decided;

  return (
    <div ref={ref} className="flex shrink-0 flex-col gap-1.5 border-t px-3 py-2.5 text-xs">
      <div className="flex gap-2">
        <Info aria-hidden className="mt-px size-4 shrink-0 text-muted-foreground" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p id={ruleId} className="text-foreground">
            {internal
              ? t(
                  'picker.policy.ruleInternal',
                  'Questions with investor or CRM data always use the Investbanq model.',
                )
              : t(
                  'picker.policy.ruleCommercial',
                  'Questions with investor or CRM data use the model you select. A commercial model sends that data to its provider.',
                )}
          </p>
          <p className="text-muted-foreground">{source}</p>
          {internal && policy.chatHasInvestorData && (
            <p className="text-muted-foreground">
              {t(
                'picker.policy.chatHasInvestorData',
                'This chat includes investor data, so replies here use the Investbanq model.',
              )}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 ps-6">
        {offerNewChat && (
          <button
            type="button"
            onClick={() => {
              onAction();
              policy.onStartNewChat?.(shown.id);
            }}
            className="inline-flex items-center gap-1 rounded-sm font-medium text-foreground outline-none hover:underline focus-visible:ring-1 focus-visible:ring-ring"
          >
            {t('picker.policy.startNewChat', 'Start a new chat with {{model}}', {
              model: shown.label,
            })}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            onAction();
            if (choose) {
              requestAnimationFrame(() => {
                policy.onChoose(shown);
              });
            } else {
              policy.onOpenSettings();
            }
          }}
          className="inline-flex items-center gap-1 rounded-sm font-medium text-foreground outline-none hover:underline focus-visible:ring-1 focus-visible:ring-ring"
        >
          {choose
            ? t('picker.policy.choose', 'Choose data policy…')
            : policy.canChange
              ? t('picker.policy.change', 'Change data policy')
              : t('picker.policy.view', 'View data policy')}
          {!choose && <ArrowRight aria-hidden className="size-3.5 rtl:-scale-x-100" />}
        </button>
      </div>
    </div>
  );
}
